import React, { useState, useRef, useEffect, Fragment } from "react";
import { useLazyQuery, useQuery } from "@apollo/client";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Stack,
  useTheme,
  useMediaQuery,
  Skeleton,
  IconButton,
  CircularProgress,
  Button,
} from "@mui/material";
import {
  Search as SearchIcon,
  School as SchoolIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import {
  GET_COURSES_SEARCHING,
  GET_UNIVERSITIES,
} from "../../../../apollo-client/queries";
import SearchCourse from "./search-course";
import { FormattedMessage, useIntl } from "react-intl";
import { useStore } from "../../../../store/store";
import { useIsMobile } from "../../../../hooks/state-checkers/use-is-mobile";
import { SuggestNewCourseBox } from "./SuggestNewCourseBox";

export default function SearchCoursesNav({
  escapeToggle,
  makeGroupVisibleInCourses,
  visibleCourses,
  setMakeGroupVisibleInCourses,
  setVisibleCourses,
}) {
  const intl = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const searchCourseHeadline = intl.formatMessage({
    id: "courses-nav.search-course",
  });
  const yourCoursePlaceholder = intl.formatMessage({
    id: "courses-nav.your-course-placeholder",
  });

  const selectedUniversity = useStore((state) => state.sCourseNavSelectedUni);
  const queryFilter = useStore((state) => state.sCourseNavSelectedUni);
  const searchText = useStore((state) => state.ScourseNavSearchText);

  const [isSuggestCourseModeOpen, setIsSuggestCourseModeOpen] = useState(false);
  const [isFirstRender, setIsFirstRender] = useState(true);

  const {
    data: dataUniversities,
    error: errorUniversities,
    loading: loadingUniversities,
  } = useQuery(GET_UNIVERSITIES);

  const [getCoursesSearching, { data: courseData, error, loading, fetchMore }] =
    useLazyQuery(GET_COURSES_SEARCHING, {
      variables: { title: searchText, university: queryFilter.name },
      notifyOnNetworkStatusChange: true,
    });

  const ref = useRef(null);
  const { menuIsOpen } = useStore();

  // Custom outside click handler that doesn't close when interacting with dropdowns
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuIsOpen) {
        return;
      }
      
      const target = event.target;
      const isSelectElement = target.closest('.MuiSelect-root') || 
                             target.closest('.MuiMenu-root') || 
                             target.closest('.MuiPaper-root') ||
                             target.closest('[role="presentation"]') ||
                             target.closest('[role="menu"]') ||
                             target.closest('[role="option"]');
      
      if (ref.current && !ref.current.contains(event.target) && !isSelectElement) {
        escapeToggle();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuIsOpen, escapeToggle]);

  const handleUniversitySelect = (university) => {
    useStore.setState({
      sCourseNavSelectedUni: university,
      ScourseNavQueryFilter: university,
    });
    if (
      courseData &&
      university.name !== courseData.courses.courses[0]?.university
    )
      setIsFirstRender(true);
  };

  const clearUniSelection = () => {
    useStore.setState({ sCourseNavSelectedUni: { name: "" } });
    useStore.setState({ ScourseNavSearchText: "" });
    setIsSuggestCourseModeOpen(false);
  };

  const scrollCoursesRef = useRef(null);
  const handleFetchMoreCourses = async () => {
    const { current } = scrollCoursesRef;
    if (
      !loading &&
      courseData.courses.courses.length < courseData.courses.coursesCount &&
      current.scrollTop + current.clientHeight >= current.scrollHeight
    ) {
      await fetchMore({
        variables: {
          limit: 15,
          skip: courseData.courses.courses.length,
        },
        // updateQuery: (prev, { fetchMoreResult }) => {
        //   if (
        //     !fetchMoreResult ||
        //     fetchMoreResult.courses.courses.length === 0
        //   ) {
        //     return prev;
        //   } else {
        //     return {
        //       courses: {
        //         ...prev.courses,
        //         courses: [
        //           ...prev.courses.courses,
        //           ...fetchMoreResult.courses.courses,
        //         ],
        //       },
        //     };
        //   }
        // },
      });
    }
  };

  //USEEFFECT
  useEffect(() => {
    const loadCourses = async () => {
      if (!courseData && !loading) {
        await getCoursesSearching();
      }

      if (courseData) setIsFirstRender(false);
    };
    loadCourses();
  }, [courseData, loading, getCoursesSearching, setIsFirstRender]);

  const closeCourseNav = () => {
    escapeToggle();
    if (setMakeGroupVisibleInCourses) setMakeGroupVisibleInCourses(false);
  };

  return (
    <Box
      id={"search_courses_nav_container"}
      data-testid={"search_courses_nav"}
      ref={ref}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        display: 'flex',
        flexDirection: 'column',
        width: { xs: '100%', sm: '50%' },
        minWidth: { xs: '100%', sm: 530 },
        maxWidth: { xs: '100%', sm: 700 },
        height: '100%',
        backgroundColor: 'background.paper',
        boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.15)',
        zIndex: 13,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Card sx={{ 
        borderRadius: 0,
        boxShadow: 'none',
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <CardContent sx={{ 
          p: { xs: 2, sm: 3 },
          '&:last-child': { pb: { xs: 2, sm: 3 } }
        }}>
          <Stack 
            direction="row" 
            alignItems="center" 
            justifyContent="space-between"
            spacing={2}
          >
            {/* Mobile back button */}
            {isMobile && (
              <IconButton 
                onClick={closeCourseNav}
                size="small"
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': { backgroundColor: 'action.hover' }
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            )}

            {/* Title */}
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <SearchIcon 
                sx={{ 
                  fontSize: { xs: 24, sm: 28 },
                  color: 'primary.main'
                }} 
              />
              <Typography 
                variant="h5" 
                fontWeight="bold"
                sx={{ 
                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  color: 'text.primary'
                }}
              >
                <FormattedMessage id="courses-nav.search-course" />
              </Typography>
            </Stack>

            {/* Desktop close button */}
            {!isMobile && (
              <IconButton 
                data-testid="search_courses_nav.close-button.desktop"
                onClick={closeCourseNav}
                size="small"
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': { backgroundColor: 'action.hover' }
                }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Content */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {isSuggestCourseModeOpen ? (
          <SuggestNewCourseBox selectedUniversityName={selectedUniversity.name} />
        ) : selectedUniversity.name !== "" ? (
          <Fragment>
            {/* Selected University */}
            <Card 
              data-testid="search_courses_nav.selected-university-container"
              sx={{ 
                m: { xs: 2, sm: 3 },
                mb: 2,
                boxShadow: 1
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <SchoolIcon sx={{ color: 'primary.main' }} />
                    <Typography variant="h6" fontWeight="bold">
                      {selectedUniversity.name}
                    </Typography>
                  </Stack>
                  <IconButton 
                    onClick={clearUniSelection}
                    size="small"
                    sx={{ 
                      color: 'text.secondary',
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Stack>
              </CardContent>
            </Card>

            {/* Search */}
            <Card sx={{ 
              m: { xs: 2, sm: 3 },
              mb: 2,
              boxShadow: 1
            }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={yourCoursePlaceholder}
                  value={searchText}
                  onChange={(event) => {
                    useStore.setState({
                      ScourseNavSearchText: event.target.value,
                    });
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{
                    'aria-label': yourCoursePlaceholder,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0,0,0,0.23)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                  }}
                />
              </CardContent>
            </Card>

            {/* Courses List */}
            <Box 
              ref={scrollCoursesRef}
              onScroll={handleFetchMoreCourses}
              data-testid={"search_courses_nav.course-container"}
              sx={{ 
                flex: 1,
                overflow: 'auto',
                px: { xs: 2, sm: 3 },
                pb: 2
              }}
            >
              {isFirstRender && loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              )}
              
              {!error && !isFirstRender && courseData?.courses?.courses && (
                <Stack spacing={1}>
                  {courseData.courses.courses.map((course) => (
                    <SearchCourse
                      courseData={courseData}
                      course={course}
                      key={course._id}
                      makeGroupVisibleInCourses={makeGroupVisibleInCourses}
                      visibleCourses={visibleCourses}
                      setVisibleCourses={setVisibleCourses}
                      selectedUniversity={selectedUniversity}
                      closeCourseNav={closeCourseNav}
                    />
                  ))}
                </Stack>
              )}

              {!isFirstRender && loading && selectedUniversity.name !== "" && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
            </Box>

            {/* Suggest New Course Button */}
            <Box sx={{ p: { xs: 2, sm: 3 }, pt: 0 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setIsSuggestCourseModeOpen(true)}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                <FormattedMessage id="courses-nav.search-course.suggest-new-course" />
              </Button>
            </Box>
          </Fragment>
        ) : (
          /* University Selection */
          <Box 
            data-testid="search_courses_nav.university-selection-container"
            sx={{ p: { xs: 2, sm: 3 } }}
          >
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ mb: 3, textAlign: 'center' }}
            >
              <FormattedMessage id="courses-nav.select-university" defaultMessage="Select a University" />
            </Typography>
            
            {loadingUniversities ? (
              <Stack spacing={2}>
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} variant="rectangular" height={60} sx={{ borderRadius: 1 }} />
                ))}
              </Stack>
            ) : !errorUniversities && dataUniversities?.universities ? (
              <Stack spacing={1}>
                {dataUniversities.universities.map((university, i) => (
                  <Card 
                    key={university._id || i}
                    sx={{ 
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        transform: 'translateY(-1px)',
                        boxShadow: 2,
                      }
                    }}
                    onClick={() => handleUniversitySelect(university)}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <SchoolIcon sx={{ color: 'primary.main' }} />
                        <Typography variant="body1" fontWeight={500}>
                          {university.name}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  <FormattedMessage id="courses-nav.no-universities" defaultMessage="No universities available" />
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

